import React, { useEffect } from 'react';
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { cn } from "../../lib/utils";
import { useWorkflowStore } from "../../store/useWorkflowStore";
import { TargetFacilitySelector } from "./selectors/TargetFacilitySelector";
import { TargetUseCaseSelector } from "./selectors/TargetUseCaseSelector";
import { ImportTypeSelector } from "./selectors/ImportTypeSelector";
import { TargetAuthorSelector } from "./selectors/TargetAuthorSelector";
import { useToast } from "../../lib/use-toast";

/**
 * TargetInstanceForm component
 *
 * Simplified form for selecting target details using SERVER_CONFIG endpoint
 * No need to select instance/client/database - uses configured DWI instance directly
 */
interface TargetInstanceFormProps {
  onNext: () => void;
}

const TargetInstanceForm: React.FC<TargetInstanceFormProps> = ({ onNext }) => {
  const {
    isFormValid,
    setIsFormValid,
    targetFacilityId,
    targetUseCaseId,
    targetImportType,
    targetChecklistId,
    targetAuthorId
  } = useWorkflowStore();

  const { toast } = useToast();

  // Update form validity based on required selections
  useEffect(() => {
    let isValid =
      !!targetFacilityId &&
      !!targetUseCaseId &&
      !!targetImportType &&
      !!targetAuthorId;

    // If revision is selected, also require targetChecklistId
    if (targetImportType === 'revision') {
      isValid = isValid && !!targetChecklistId;
    }

    setIsFormValid(isValid);
  }, [
    targetFacilityId,
    targetUseCaseId,
    targetImportType,
    targetChecklistId,
    targetAuthorId,
    setIsFormValid
  ]);

  // Handle form submission
  const handleNext = () => {
    if (!isFormValid) {
      let errorMessage = "Please complete all required fields";
      if (!targetFacilityId) errorMessage = "Please select target facility";
      else if (!targetUseCaseId) errorMessage = "Please select target use case";
      else if (!targetImportType) errorMessage = "Please select import type";
      else if (targetImportType === 'revision' && !targetChecklistId) errorMessage = "Please select checklist to revise";
      else if (!targetAuthorId) errorMessage = "Please select target author";

      toast({
        variant: "destructive",
        title: "Validation Error",
        description: errorMessage,
      });
      return;
    }

    // All validations passed, proceed to next step
    onNext();
  };

  return (
    <div>
      <h2 className="text-xl font-semibold text-foreground mb-4">Target</h2>
      <p className="text-muted-foreground mb-6">
        Select the target facility and details for the migration. Using configured DWI instance.
      </p>

      <div className="flex flex-col space-y-4">
        {/* Target Facility Selector */}
        <TargetFacilitySelector className="w-full" />

        {/* Target Use Case Selector */}
        <TargetUseCaseSelector className="w-full" />

        {/* Import Type Selector (New vs Revision) */}
        <ImportTypeSelector className="w-full" />

        {/* Target Author Selector */}
        <TargetAuthorSelector className="w-full" />
      </div>

      <div className="mt-8 flex justify-end">
        <Button
          onClick={handleNext}
          disabled={false}
          variant={isFormValid ? "default" : "outline"}
          className="px-6"
        >
          Next
        </Button>
      </div>
    </div>
  );
};

export default TargetInstanceForm;
